/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { Music, ListMusic, Guitar } from 'lucide-react';
import IntervalsLegend from './IntervalsLegend';
import BackingTrackPanel from './JamTrack/BackingTrackPanel';
import MobileChordFingerings from './MobileChordFingerings';
import { NoteCollection, ChordFingering } from '../api';

interface MobileContentAccordionProps {
  // Core data
  noteCollection: NoteCollection | null;
  currentKey: string;
  currentScale: string;      // Display name for UI
  currentScaleValue: string; // Raw value for API calls
  isChord: boolean;

  // Jam tracks
  onTrackSelect: (track: any) => void;
  availableTracksCount: number;
  onTracksCountChange: (count: number) => void;
  isAnonymous: boolean;

  // Chord fingerings (only shown in chord mode with standard tuning)
  showFingerings: boolean;
  chordRoot?: string;
  chordType?: string;
  onFingeringSelect?: (fingering: ChordFingering | null) => void;
  selectedFingering?: ChordFingering | null;

  // Scale animation (only in scale mode)
  isAnimating?: boolean;
  onToggleAnimation?: () => void;

  // Default open sections
  defaultOpenSections?: string[];
}

/**
 * MobileContentAccordion - Collapsible sections for mobile layout
 *
 * Provides a clean, space-efficient way to access all content below the fretboard
 * on mobile devices. Users can expand/collapse sections as needed.
 */
const MobileContentAccordion: React.FC<MobileContentAccordionProps> = ({
  noteCollection,
  currentKey,
  currentScale,
  currentScaleValue,
  isChord,
  onTrackSelect,
  availableTracksCount,
  onTracksCountChange,
  isAnonymous,
  showFingerings,
  chordRoot,
  chordType,
  onFingeringSelect,
  selectedFingering,
  isAnimating,
  onToggleAnimation,
  defaultOpenSections = ['legend']
}) => {
  return (
    <div className="w-full bg-card border-t mt-2">
      <Accordion
        type="multiple"
        defaultValue={defaultOpenSections}
        className="w-full"
      >
        {/* Notes & Intervals - Usually open by default */}
        <AccordionItem value="legend" className="border-b">
          <AccordionTrigger className="px-3 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Music className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Notes & Intervals</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-3">
            <IntervalsLegend
              noteCollection={noteCollection}
              currentKey={currentKey}
              currentScale={currentScale}
              isAnimating={isAnimating}
              onToggleAnimation={onToggleAnimation}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Jam Tracks - Only in scale mode, collapsed by default */}
        {!isChord && availableTracksCount > 0 && (
          <AccordionItem value="jamtracks" className="border-b">
            <AccordionTrigger className="px-3 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <ListMusic className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">Jam Tracks</span>
                <Badge variant="secondary" className="ml-1 text-xs">
                  {availableTracksCount}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-0">
              <BackingTrackPanel
                currentKey={currentKey}
                currentScale={currentScaleValue}
                onTrackSelect={onTrackSelect}
                isVisible={true}
                inMainArea={true}
                onTracksCountChange={onTracksCountChange}
                isAnonymous={isAnonymous}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Chord Fingerings - Only in chord mode with standard tuning */}
        {showFingerings && isChord && chordRoot && chordType && onFingeringSelect && (
          <AccordionItem value="fingerings" className="border-b">
            <AccordionTrigger className="px-3 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <Guitar className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium">Chord Shapes</span>
                {selectedFingering && (
                  <Badge variant="default" className="ml-1 text-xs bg-purple-500">
                    1 selected
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-0">
              <MobileChordFingerings
                chordRoot={chordRoot}
                chordType={chordType}
                onFingeringSelect={onFingeringSelect}
                selectedFingering={selectedFingering}
              />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
};

export default MobileContentAccordion;
